import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { memoSchema, type MemoInput } from 'shared'
import RadioGroup from '../ui/RadioGroup'
import Textarea from '../ui/Textarea'
import Button from '../ui/Button'
import type { PatientRecord } from 'shared'

interface Step3Props {
  initialData?: Partial<PatientRecord>
  onNext: (data: Partial<PatientRecord>) => void
  onBack: () => void
}

const Step3Memo = ({ initialData, onNext, onBack }: Step3Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<MemoInput>({
    mode: 'onBlur',
    resolver: zodResolver(memoSchema),
    defaultValues: initialData?.memo,
  })

  const onSubmit = (data: MemoInput) => {
    onNext({ memo: data })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-5xl mx-auto">
      <h2 className="text-xl font-medium text-neutral-800 mb-6">施術情報</h2>

      <div className="space-y-5">
        {/* 服のサイズ */}
        <Controller
          name="clothingSize"
          control={control}
          render={({ field }) => (
            <RadioGroup
              label="服のサイズ"
              name="clothingSize"
              options={[
                { value: 'S', label: 'S' },
                { value: 'M', label: 'M' },
                { value: 'L', label: 'L' },
                { value: 'LL', label: 'LL' },
                { value: '3L', label: '3L' },
              ]}
              value={field.value}
              onChange={field.onChange}
              error={errors.clothingSize?.message}
              helperText="施術着のサイズ確認のため"
              required
            />
          )}
        />

        {/* リラクゼーション経験 */}
        <Controller
          name="relaxationExperience"
          control={control}
          render={({ field }) => (
            <RadioGroup
              label="リラクゼーション経験"
              name="relaxationExperience"
              options={[
                { value: '有', label: '有' },
                { value: '無', label: '無' },
              ]}
              value={field.value}
              onChange={field.onChange}
              error={errors.relaxationExperience?.message}
              helperText="過去のリラクゼーション利用歴"
              required
            />
          )}
        />

        {/* 主訴 */}
        <Textarea
          {...register('chiefComplaint')}
          label="主訴（気になる症状）"
          placeholder="例: 肩こり、腰痛、眼精疲労"
          error={errors.chiefComplaint?.message}
          helperText="特に気になる症状や部位"
          required
        />
      </div>

      <div className="mt-8 flex justify-between gap-4">
        <Button type="button" variant="secondary" size="large" onClick={onBack} className="min-w-[140px]">
          戻る
        </Button>
        <Button type="submit" variant="primary" size="large" className="min-w-[200px]">
          次へ進む
        </Button>
      </div>
    </form>
  )
}

export default Step3Memo
